import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import ConfigKeyService from '../../../../services/config-key.service';
import ProductService from '../../../../services/product.service';
import { Modules, AuthRoutes } from '../../../../enums';

type DataType = 'text' | 'number' | 'boolean' | 'date' | 'dropdown';

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface ConfigKeyFormState {
  key_code: string;
  display_label: string;
  data_type: DataType;
  default_value: string;
  is_mandatory: boolean;
  scope: 'global' | 'product' | 'tenant';
  product_id: number | '';
  dropdownOptionsCsv: string;
  group_category: string;
  display_order: number | string;
  notes: string;
}

const initialForm: ConfigKeyFormState = {
  key_code: '',
  display_label: '',
  data_type: 'text',
  default_value: '',
  is_mandatory: false,
  scope: 'global',
  product_id: '',
  dropdownOptionsCsv: '',
  group_category: '',
  display_order: 0,
  notes: '',
};

const ConfigKeyForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<ConfigKeyFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [products, setProducts] = useState<ProductOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    ConfigKeyService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        let dropdownOptionsCsv = '';
        if (row.data_type === 'dropdown' && row.validation_rule) {
          try {
            const parsed = JSON.parse(row.validation_rule);
            if (Array.isArray(parsed)) dropdownOptionsCsv = parsed.join(', ');
          } catch {
            dropdownOptionsCsv = '';
          }
        }
        setFormData({
          key_code: row.key_code ?? '',
          display_label: row.display_label ?? '',
          data_type: row.data_type ?? 'text',
          default_value: row.default_value ?? '',
          is_mandatory: row.is_mandatory ?? false,
          scope: row.scope ?? 'global',
          product_id: row.product_id ?? '',
          dropdownOptionsCsv,
          group_category: row.group_category ?? '',
          display_order: row.display_order ?? 0,
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const dropdownOptions = useMemo(
    () =>
      formData.dropdownOptionsCsv
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
    [formData.dropdownOptionsCsv]
  );

  const validate = useCallback(
    (data: ConfigKeyFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.key_code.trim()) nextErrors.key_code = t('masters.configKeys.form.pageTexts.fields.keyCode.errors.required');
      if (!data.display_label.trim()) nextErrors.display_label = t('masters.configKeys.form.pageTexts.fields.displayLabel.errors.required');
      if (!data.notes.trim()) nextErrors.notes = t('masters.configKeys.form.pageTexts.fields.notes.errors.required');
      if (data.scope === 'product' && !data.product_id) {
        nextErrors.product_id = t('masters.configKeys.form.pageTexts.fields.product.errors.required');
      }
      if (data.data_type === 'dropdown' && dropdownOptions.length === 0) {
        nextErrors.dropdownOptionsCsv = t('masters.configKeys.form.pageTexts.fields.validationRule.errors.required');
      }
      return nextErrors;
    },
    [t, dropdownOptions]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'is_mandatory' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = {
          key_code: formData.key_code,
          display_label: formData.display_label,
          data_type: formData.data_type,
          default_value: formData.default_value || undefined,
          is_mandatory: formData.is_mandatory,
          scope: formData.scope,
          product_id: formData.scope === 'product' && formData.product_id ? Number(formData.product_id) : undefined,
          validation_rule: formData.data_type === 'dropdown' ? JSON.stringify(dropdownOptions) : undefined,
          group_category: formData.group_category || undefined,
          display_order: Number(formData.display_order) || 0,
          notes: formData.notes,
        };

        if (edit && id) {
          await ConfigKeyService.update(id, payload);
          toast.success(<Toast message={t('masters.configKeys.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await ConfigKeyService.create(payload);
          toast.success(<Toast message={t('masters.configKeys.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.configKeys}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, dropdownOptions, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.configKeys.form.pageTexts.editTitle') : t('masters.configKeys.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="key_code"
            name="key_code"
            label={t('masters.configKeys.form.pageTexts.fields.keyCode.label')}
            placeholder={t('masters.configKeys.form.pageTexts.fields.keyCode.placeholder')}
            required
            value={formData.key_code}
            error={!!errors.key_code}
            errorMessage={errors.key_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="display_label"
            name="display_label"
            label={t('masters.configKeys.form.pageTexts.fields.displayLabel.label')}
            placeholder={t('masters.configKeys.form.pageTexts.fields.displayLabel.placeholder')}
            required
            value={formData.display_label}
            error={!!errors.display_label}
            errorMessage={errors.display_label}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="data_type">
            {t('masters.configKeys.form.pageTexts.fields.dataType.label')}
          </label>
          <select
            id="data_type"
            name="data_type"
            className="form-select"
            value={formData.data_type}
            onChange={(e) => handleChange(e, 'data_type')}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="date">Date</option>
            <option value="dropdown">Dropdown</option>
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="scope">
            {t('masters.configKeys.form.pageTexts.fields.scope.label')}
          </label>
          <select id="scope" name="scope" className="form-select" value={formData.scope} onChange={(e) => handleChange(e, 'scope')}>
            <option value="global">Global</option>
            <option value="product">Product</option>
            <option value="tenant">Tenant</option>
          </select>
        </div>
        {formData.scope === 'product' && (
          <div className="col-md-4">
            <label className="form-label d-block" htmlFor="product_id">
              {t('masters.configKeys.form.pageTexts.fields.product.label')}
            </label>
            <select
              id="product_id"
              name="product_id"
              className={`form-select ${errors.product_id ? 'is-invalid' : ''}`}
              value={formData.product_id}
              onChange={(e) => handleChange(e, 'product_id')}
            >
              <option value="">{t('masters.configKeys.form.pageTexts.fields.product.placeholder')}</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_code} — {product.product_name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="col-md-4">
          <CustomizedSwitch
            label={t('masters.configKeys.form.pageTexts.fields.isMandatory.label')}
            name="is_mandatory"
            checked={formData.is_mandatory}
            onChange={handleChange}
          />
        </div>

        {formData.data_type === 'dropdown' && (
          <div className="col-md-8">
            <Input
              id="dropdownOptionsCsv"
              name="dropdownOptionsCsv"
              label={t('masters.configKeys.form.pageTexts.fields.validationRule.label')}
              placeholder={t('masters.configKeys.form.pageTexts.fields.validationRule.placeholder')}
              value={formData.dropdownOptionsCsv}
              error={!!errors.dropdownOptionsCsv}
              errorMessage={errors.dropdownOptionsCsv}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="col-md-4">
          <label className="form-label d-block">{t('masters.configKeys.form.pageTexts.fields.defaultValue.label')}</label>
          {formData.data_type === 'boolean' ? (
            <CustomizedSwitch
              label=""
              name="default_value"
              checked={formData.default_value === 'true'}
              onChange={(e: any) => setFormData((prev) => ({ ...prev, default_value: e.target.checked ? 'true' : 'false' }))}
            />
          ) : formData.data_type === 'dropdown' ? (
            <select
              className="form-select"
              value={formData.default_value}
              onChange={(e) => handleChange(e, 'default_value')}
            >
              <option value="">{t('masters.configKeys.form.pageTexts.fields.defaultValue.placeholder')}</option>
              {dropdownOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <Input
              id="default_value"
              name="default_value"
              type={formData.data_type === 'number' ? 'number' : formData.data_type === 'date' ? 'date' : 'text'}
              value={formData.default_value}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="col-md-4">
          <Input
            id="group_category"
            name="group_category"
            label={t('masters.configKeys.form.pageTexts.fields.groupCategory.label')}
            placeholder={t('masters.configKeys.form.pageTexts.fields.groupCategory.placeholder')}
            value={formData.group_category}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="display_order"
            name="display_order"
            type="number"
            min="0"
            label={t('masters.configKeys.form.pageTexts.fields.displayOrder.label')}
            value={formData.display_order}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.configKeys.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.configKeys.form.pageTexts.fields.notes.placeholder')}
            required
            value={formData.notes}
            error={!!errors.notes}
            errorMessage={errors.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.configKeys.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.configKeys}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.configKeys.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default ConfigKeyForm;
